import Header from '../../../header';
import { Card } from '../../Card';
import AdminHeader from '../admin/AdminHeader';
import { ApiKey } from '../../../shared/types';
import { useMemo, useState, useCallback } from 'react';
import Loading from 'src/components/Loading';
import Button from 'src/components/Button';
import { PlusIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import ApiKeyModal from 'src/components/modals/ApiKeyModal';
import ApiDocModal from 'src/components/modals/ApiDocModal';
import { Table, ColumnProps } from '../../table/Table';
import { useTableData, PaginationParams, PaginatedResponse, SortDirection } from '../../table/useTableData';
import { apiDelete, apiGet } from '../../../utils/api';
import { useCurrentOrganization } from 'src/contexts/OrganizationContext';

const ApiKeyTab = () => {
  const organization = useCurrentOrganization();
  const [ showApiKeyModal, setShowApiKeyModal ] = useState(false);
  const [ showApiDocModal, setShowApiDocModal ] = useState(false);

  const createButton = useMemo(() => {
    return (
      <Button
        text="Create API Key"
        onClick={() => {
          setShowApiKeyModal(true);
        }}
        icon={<PlusIcon className="w-4 h-4 stroke-2" />}
      />
    );
  }, []);

  const docsButton = useMemo(() => {
    return (
      <Button
        text="API Documentation"
        onClick={() => {
          setShowApiDocModal(true);
        }}
        icon={<DocumentTextIcon className="w-4 h-4 stroke-2" />}
        className="bg-slate-600 hover:bg-slate-700"
      />
    );
  }, []);

  const getApiKeys = useCallback(async (params: PaginationParams): Promise<PaginatedResponse<ApiKey>> => {
    const response = await apiGet(`/api/${organization.id}/api-keys`, {
      page: params.page,
      pageSize: params.pageSize,
      sortProperty: params.sortProperty,
      sortDirection: params.sortDirection,
    });
    const data = await response.json();
    return data;
  }, [ organization.id ]);

  const tableData = useTableData({
    tableId: 'api-keys',
    getData: getApiKeys,
    initialSortField: 'name',
    initialSortDirection: SortDirection.ASC,
  });

  const deleteApiKey = useCallback(async (name: string) => {
    await apiDelete(`/api/${organization.id}/api-keys`, { name });
    tableData.refresh();
  }, [ organization.id, tableData ]);

  const columns: ColumnProps<ApiKey>[] = useMemo(() => [
    {
      title: "ID",
      sortKey: 'id',
      convertToCellData: (apiKey: ApiKey) => (
        <span className="text-sm text-gray-900">{apiKey.id}</span>
      )
    },
    {
      title: "Name",
      sortKey: 'name',
      convertToCellData: (apiKey: ApiKey) => (
        <span className="text-sm font-medium text-gray-900">{apiKey.name}</span>
      )
    },
    {
      title: "Created At",
      sortKey: 'created_at',
      convertToCellData: (apiKey: ApiKey) => (
        <span className="text-sm text-gray-900">
          {new Date(apiKey.created_at).toLocaleString('en-US', {
            timeZone: 'America/New_York',
            timeZoneName: "short",
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })}
        </span>
      )
    },
    {
      title: "Created By",
      sortKey: 'user_email',
      convertToCellData: (apiKey: ApiKey) => (
        <span className="text-sm text-gray-900">{apiKey.user_email}</span>
      )
    },
    {
      title: "Last Used At",
      sortKey: 'last_used_at',
      convertToCellData: (apiKey: ApiKey) => (
        <span className="text-sm text-gray-900">
          {apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleString('en-US', {
            timeZone: 'America/New_York',
            timeZoneName: "short",
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }).replace(/(\d+)\/(\d+)\/(\d+),/, '$1/$2/$3,') : 'Never'}
        </span>
      )
    },
  ], []);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header isAdminPage={true} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AdminHeader
          title="API Keys"
          description="Manage your organization's API keys"
          organization={organization}
        />

        <Card className="w-full p-8">
          {tableData.loading ? <Loading message="Loading API keys..." /> :
          (
            tableData.items && tableData.items.length > 0 ? (
              <>
                <div className="flex justify-end mb-4 gap-3">
                  {docsButton}
                  {createButton}
                </div>
                <Table
                  tableData={tableData}
                  columns={columns}
                  rowId={(apiKey: ApiKey) => `${apiKey.id}-${apiKey.name}`}
                  headerClass="bg-gray-50"
                  headerColor={false}
                  onDelete={(apiKey: ApiKey) => {
                    deleteApiKey(apiKey.name);
                  }}
                />
              </>
            ) : (
              <>
                <div className="text-center space-y-4">
                  <p className="text-gray-500 text-lg">No API Keys available</p>
                  <p className="text-gray-500 text-lg">Create an API Key to get started</p>
                  {createButton}
                </div>
              </>
            )
          )}
        </Card>
        {showApiKeyModal && (
          <ApiKeyModal
            onClose={() => {
              setShowApiKeyModal(false);
              tableData.refresh();
            }}
          />
        )}
        {showApiDocModal && organization.id && (
          <ApiDocModal
            onClose={() => {
              setShowApiDocModal(false);
            }}
            org_id={organization.id.toString()}
          />
        )}
      </div>
    </div>
  );
};

export default ApiKeyTab;
